import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { UserStatus } from '$/core/enums';
import './index.scss';

// Icons for each status - you can replace these with actual SVG imports
const StatusIcon: React.FC<{ status: UserStatus }> = ({ status }) => {
  switch (status) {
    case UserStatus.Available:
      return <span className="status-icon available">●</span>;
    case UserStatus.Break:
      return <span className="status-icon break">◐</span>;
    case UserStatus.OutOfOffice:
      return <span className="status-icon out-of-office">✕</span>;
    case UserStatus.Busy:
      return <span className="status-icon busy">⊝</span>;
    default:
      return null;
  }
};

export interface IUserStatusDropdownProps {
  currentStatus: UserStatus;
  onStatusChange: (status: UserStatus) => void;
  isUpdating?: boolean;
}

const UserStatusDropdown: React.FC<IUserStatusDropdownProps> = ({
  currentStatus,
  onStatusChange,
  isUpdating = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleStatusSelect = (status: UserStatus) => {
    onStatusChange(status);
    setIsOpen(false);
  };

  const statuses = [UserStatus.Available, UserStatus.Break, UserStatus.OutOfOffice, UserStatus.Busy];

  return (
    <div className="user-status-dropdown" ref={dropdownRef}>
      <button
        className={classNames('status-trigger', { active: isOpen, updating: isUpdating })}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
        aria-label={isUpdating ? 'Updating status…' : undefined}>
        {isUpdating ? <span className="status-spinner" aria-hidden="true" /> : <StatusIcon status={currentStatus} />}
        <span className={classNames('status-text', currentStatus.toLowerCase().replaceAll(/\s+/g, ''))}>
          {isUpdating ? 'Updating…' : currentStatus}
        </span>
        {!isUpdating && <span className="status-chevron">▼</span>}
      </button>

      {isOpen && (
        <div className="status-dropdown-menu">
          {statuses.map(status => (
            <button
              key={status}
              className={classNames('status-option', {
                selected: status === currentStatus,
                [status.toLowerCase().replaceAll(/\s+/g, '')]: true,
              })}
              onClick={() => handleStatusSelect(status)}>
              <StatusIcon status={status} />
              <span>{status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserStatusDropdown;
